import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        if (orderId) {
          setStatus('success');
          clearCart();
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/stripe/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, orderId, attempts, clearCart]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-white">
      <div className="container-custom max-w-2xl text-center">
        {status === 'checking' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="spinner mx-auto mb-8" />
            <h1 className="text-3xl font-bold tracking-tight">
              Processing Payment...
            </h1>
            <p className="mt-4 text-neutral-500">Please wait while we confirm your payment.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              <span className="text-[#e84628]">Thank</span> You!
            </h1>
            <p className="mt-4 text-neutral-500 text-lg">
              Your order has been confirmed. We'll send you an email with your order details.
            </p>

            <div className="mt-12 p-8 bg-neutral-50 border border-neutral-200">
              <div className="flex items-center gap-4 justify-center">
                <Package size={24} className="text-neutral-600" />
                <div className="text-left">
                  <p className="text-neutral-400 text-sm uppercase tracking-wider">Estimated Delivery</p>
                  <p className="font-semibold">5-7 Business Days</p>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/shop"
                data-testid="continue-shopping"
                className="btn-primary inline-flex items-center gap-2"
              >
                Continue Shopping
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/"
                className="btn-secondary"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        )}

        {status === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold tracking-tight">
              Payment Processing
            </h1>
            <p className="mt-4 text-neutral-500">
              Your payment is still being processed. Please check your email for confirmation.
            </p>
            <Link to="/" className="btn-primary mt-8 inline-block">
              Back to Home
            </Link>
          </motion.div>
        )}

        {status === 'failed' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold tracking-tight text-red-600">
              Payment Failed
            </h1>
            <p className="mt-4 text-neutral-500">
              There was an issue processing your payment. Please try again.
            </p>
            <Link to="/checkout" className="btn-primary mt-8 inline-block">
              Try Again
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
